/**
 * The matter overview.
 *
 * ===========================================================================
 * `null` AND `[]` ARE DIFFERENT, AND THE DIFFERENCE IS THE POINT
 * ===========================================================================
 * `open_tasks`, `upcoming_critical_dates`, `outstanding_documents` and
 * `financial_position` are `null` when the reader holds no capability for that module,
 * and `[]` when they hold it and there is nothing.
 *
 *   null -> do not render the panel at all
 *   []   -> render the panel with its empty state
 *
 * Collapsing them would show Finance an empty "Critical dates" panel on a matter
 * whose limitation dates it is deliberately not permitted to know exist. The absence
 * of the panel IS the answer, and it is the kernel's answer, not this component's.
 * ===========================================================================
 */

import { STAFF, qk, useApi, useApiMutation, type CaseDetail, type CaseFinancialPosition } from '@legalcrm/api';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, cn } from '@legalcrm/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Can } from '@/auth/permissions';
import { useAuth } from '@/auth/AuthProvider';
import { CaseTeamCard } from './CaseTeamCard';

import { formatDate, formatDateTime, formatMoney, formatRelative, humanise, isPast } from '@/lib/format';

// Role labels moved to CaseTeamCard, which is now the only thing that renders them.

export function CaseOverviewTab({ caseRecord }: { caseRecord: CaseDetail }) {
  const api = useApi();
  const [completing, setCompleting] = useState<string | null>(null);

  const completeMilestone = useApiMutation<unknown, string>({
    mutationFn: (milestoneId) =>
      api.post(`${STAFF.caseMilestoneComplete(caseRecord.id, milestoneId)}/`),
    invalidates: [qk.detail('cases', caseRecord.id)],
  });

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="flex flex-col gap-4 lg:col-span-2">
        <Card>
          <CardHeader title="Summary" />
          <CardBody className="flex flex-col gap-4">
            {caseRecord.description ? (
              <p className="whitespace-pre-line text-base text-ink">{caseRecord.description}</p>
            ) : (
              <p className="text-sm text-ink-subtle">No description recorded.</p>
            )}

            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              <Detail label="Client">
                <Link
                  to={`/clients/${caseRecord.client}`}
                  className="text-primary hover:underline"
                >
                  {caseRecord.client_name}
                </Link>
              </Detail>
              <Detail label="Service">{caseRecord.service_name}</Detail>
              <Detail label="Opened">{formatDate(caseRecord.opened_at)}</Detail>
              <Detail label="Target completion">
                {caseRecord.target_completion_at ? (
                  <span
                    className={cn(
                      isPast(caseRecord.target_completion_at) && 'font-medium text-danger',
                    )}
                  >
                    {formatDate(caseRecord.target_completion_at)}
                  </span>
                ) : (
                  '—'
                )}
              </Detail>
              <Detail label="Work gate">{humanise(caseRecord.work_gate)}</Detail>
              <Detail label="Last updated">{formatDateTime(caseRecord.updated_at)}</Detail>
            </dl>

            <WorkGateNotice caseRecord={caseRecord} />

            {caseRecord.status === 'on_hold' && caseRecord.on_hold_reason && (
              <div className="rounded border border-warning/30 bg-warning-subtle px-3 py-2">
                <p className="text-sm font-medium text-ink">On hold</p>
                <p className="text-sm text-ink-muted">{caseRecord.on_hold_reason}</p>
              </div>
            )}
          </CardBody>
        </Card>

        {/* null => the reader holds no critical_date capability. Do not render. */}
        {caseRecord.upcoming_critical_dates !== null && (
          <Card>
            <CardHeader
              title="Critical dates"
              description="Missing one of these has legal consequence. Never shown to a client."
            />
            {caseRecord.upcoming_critical_dates.length === 0 ? (
              <EmptyState size="sm" title="No open critical dates" />
            ) : (
              <ul className="flex flex-col divide-y">
                {caseRecord.upcoming_critical_dates.map((date) => (
                  <li
                    key={date.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-base text-ink">{humanise(date.kind)}</p>
                      <p className="text-xs text-ink-muted">
                        {formatDate(date.due_on)} · {formatRelative(date.due_on)}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {date.is_overdue && (
                        <Badge tone="danger" size="sm">
                          Overdue
                        </Badge>
                      )}
                      {/*
                        Verification is independent of the person who set the date,
                        and an unverified date is a live exposure rather than a
                        formatting detail: nobody has checked the calculation, so the
                        firm does not yet know whether the date is even right.
                      */}
                      <Badge tone={date.verified_at ? 'success' : 'warning'} size="sm">
                        {date.verified_at ? 'Verified' : 'Unverified'}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {caseRecord.open_tasks !== null && (
          <Card>
            <CardHeader title="Open tasks" />
            {caseRecord.open_tasks.length === 0 ? (
              <EmptyState size="sm" title="No open tasks" />
            ) : (
              <ul className="flex flex-col divide-y">
                {caseRecord.open_tasks.map((task) => (
                  <li
                    key={task.id}
                    className="flex items-center justify-between gap-3 px-4 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-base text-ink">{task.title}</p>
                      <p className="text-xs text-ink-muted">{humanise(task.status)}</p>
                    </div>
                    {task.due_at && (
                      <span
                        className={cn(
                          'shrink-0 text-sm',
                          task.is_overdue ? 'font-medium text-danger' : 'text-ink-muted',
                        )}
                      >
                        {task.is_overdue ? 'Overdue' : formatRelative(task.due_at)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <CaseTeamCard caseRecord={caseRecord} />

        {caseRecord.outstanding_documents !== null && (
          <Card>
            <CardHeader title="Outstanding from the client" />
            {caseRecord.outstanding_documents.length === 0 ? (
              <EmptyState size="sm" title="Nothing outstanding" />
            ) : (
              <ul className="flex flex-col divide-y">
                {caseRecord.outstanding_documents.map((document) => (
                  <li key={document.id} className="px-4 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-base text-ink">{document.title}</p>
                      {document.is_mandatory && (
                        <Badge tone="warning" size="sm">
                          Required
                        </Badge>
                      )}
                    </div>
                    {document.due_at && (
                      <p className="text-xs text-ink-muted">
                        Due {formatDate(document.due_at)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {/* null => the reader holds no invoice.view capability. Finance data is not
            shown to somebody with no standing to see the firm's billing. */}
        {caseRecord.financial_position !== null && (
          <Card>
            <CardHeader title="Financial position" />
            <CardBody>
              <FinancialPosition position={caseRecord.financial_position} />
            </CardBody>
          </Card>
        )}

        {caseRecord.milestones.length > 0 && (
          <Card>
            <CardHeader title="Milestones" />
            <ul className="flex flex-col divide-y">
              {caseRecord.milestones.map((milestone) => (
                <li key={milestone.id} className="flex items-center gap-2.5 px-4 py-2.5">
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px]',
                      milestone.is_complete
                        ? 'border-success bg-success text-white'
                        : 'border-line-strong',
                    )}
                    aria-hidden="true"
                  >
                    {milestone.is_complete ? '✓' : ''}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-base text-ink">
                    {milestone.name ?? `Milestone ${milestone.position}`}
                  </span>
                  <span className="sr-only">
                    {milestone.is_complete ? 'Complete' : 'Not complete'}
                  </span>
                  {/* One way. A milestone is a statement that something happened, and
                      un-happening it is not a checkbox — it is a correction, which
                      belongs in the timeline rather than in a toggle. */}
                  {!milestone.is_complete && (
                    <Can module="case" action="edit">
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={completing === milestone.id}
                        onClick={() => {
                          setCompleting(milestone.id);
                          void completeMilestone
                            .mutateAsync(milestone.id)
                            .finally(() => setCompleting(null));
                        }}
                      >
                        Mark done
                      </Button>
                    </Can>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}

/**
 * Whether work on THIS matter is locked, and what would release it.
 *
 * ===========================================================================
 * THE RULE IS NOT THE ANSWER
 * ===========================================================================
 * "Work gate: agreement signed" sat in the detail list above and said what the rule was.
 * It never said whether this matter satisfied it, so it read as a locked matter even
 * when nothing was locked — and when something WAS locked, the first anybody heard of it
 * was a 409 on a stage change, naming a step with no link anywhere near it.
 *
 * `work_gate_status.reason` is empty whenever work may proceed, including where the gate
 * is `none`. So this renders nothing at all in the ordinary case. A panel that stays and
 * says "not locked" is a notice people learn to skip, and the one time it matters they
 * will skip it too.
 */
export function WorkGateNotice({ caseRecord }: { caseRecord: CaseDetail }) {
  const gate = caseRecord.work_gate_status;

  if (!gate.reason) return null;

  return (
    <div className="rounded border border-warning/40 bg-warning-subtle px-3 py-2">
      <p className="text-sm font-medium text-ink">
        {gate.gate === 'agreement_signed'
          ? 'Work is locked until the engagement letter is signed'
          : 'Work is locked on this matter'}
      </p>
      {/* The server's own wording, so this and the refusal on a stage change read the
          same sentence rather than two paraphrases of one rule. */}
      <p className="mt-0.5 text-sm text-ink-muted">{gate.reason}</p>
      <p className="mt-1 text-sm text-ink-muted">
        The matter stays open and can be prepared. It cannot move past the stage where
        work begins.
      </p>
      <div className="mt-2 flex flex-wrap gap-3 text-sm">
        {gate.agreement_id ? (
          <Link
            to={`/agreements/${gate.agreement_id}`}
            className="font-medium text-primary hover:underline"
          >
            Open the engagement letter
          </Link>
        ) : (
          gate.gate === 'agreement_signed' && (
            <Link
              to={`/clients/${caseRecord.client}`}
              className="font-medium text-primary hover:underline"
            >
              No letter exists — draft one on the client
            </Link>
          )
        )}
        {gate.gate !== 'agreement_signed' && (
          <Link
            to={`/clients/${caseRecord.client}`}
            className="font-medium text-primary hover:underline"
          >
            Open the client
          </Link>
        )}
      </div>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 text-base text-ink">{children}</dd>
    </div>
  );
}

/**
 * The money position on this matter.
 *
 * Money stays a decimal string all the way to `Intl.NumberFormat`. The only numeric
 * comparison is against zero, which is safe; parsing a decimal into a float loses
 * precision at exactly the magnitudes a ledger cares about.
 */
function FinancialPosition({ position }: { position: CaseFinancialPosition }) {
  const { baseCurrency } = useAuth();
  const currency = position.currency || baseCurrency;
  const nothingBilled =
    position.issued_invoice_count === 0 && position.draft_invoice_count === 0;

  if (nothingBilled) {
    return <p className="text-sm text-ink-subtle">Nothing invoiced yet.</p>;
  }

  const owed = Number(position.outstanding_balance) > 0;

  return (
    <dl className="flex flex-col gap-2">
      <Money label="Invoiced" value={position.invoiced_total} currency={currency} />
      <Money label="Paid" value={position.amount_paid} currency={currency} muted />
      <div className="flex items-baseline justify-between gap-3 border-t pt-2">
        <dt className="text-sm font-medium text-ink">Outstanding</dt>
        <dd
          className={cn(
            'text-base font-semibold tabular-nums',
            owed ? 'text-ink' : 'text-ink-muted',
          )}
        >
          {formatMoney(position.outstanding_balance, currency)}
        </dd>
      </div>
      {position.draft_invoice_count > 0 && (
        <p className="pt-1 text-xs text-ink-subtle">
          {position.draft_invoice_count} draft invoice
          {position.draft_invoice_count === 1 ? '' : 's'} not yet issued — drafts are not
          counted in the figures above.
        </p>
      )}
    </dl>
  );
}

function Money({
  label,
  value,
  currency,
  muted,
}: {
  label: string;
  value: string;
  currency: string;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd
        className={cn('text-base tabular-nums', muted ? 'text-ink-muted' : 'text-ink')}
      >
        {formatMoney(value, currency)}
      </dd>
    </div>
  );
}
